/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * PublicPageEditor — configure a Public Page embed section.
 * Lets the admin pick a public table, choose view type, set record count,
 * customise the heading and Show More button.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import EditorField from './_EditorField';
import DataSectionPanel from './DataSectionPanel';
import { useTheme } from '../../../theme/ThemeContext';
import { apiHelper } from '../../../hooks/usePages';

const VIEW_TYPES = [
  { value: '',         label: 'Table Default' },
  { value: 'card',     label: 'Card Grid'     },
  { value: 'list',     label: 'List / Feed'   },
  { value: 'table',    label: 'Data Table'    },
  { value: 'timeline', label: 'Timeline'      },
];

const COUNT_PRESETS = [3, 6, 9, 12];

function SectionDivider({ label }) {
  return (
    <div style={{ margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function ColorPicker({ value, onChange, presets }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {presets.map(p => (
        <button key={p.color + p.label} type="button" title={p.label} onClick={() => onChange(p.color)}
          style={{ width: 22, height: 22, borderRadius: 4, padding: 0, flexShrink: 0, cursor: 'pointer',
            border: value === p.color ? '2px solid #334155' : '1px solid #cbd5e1',
            background: p.color === '' ? 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 0 0 / 10px 10px' : p.color,
          }} />
      ))}
      <input type="color" value={value && value !== '' ? value : '#ffffff'} onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 1, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
      {value && value !== '' && <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{value}</span>}
      {value !== '' && <button type="button" onClick={() => onChange('')} style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Reset</button>}
    </div>
  );
}

export default function PublicPageEditor({ section, dispatch, appId }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const primaryHex = theme.primary || '#4e2e80';

  const update = patch => dispatch({
    type: 'UPDATE_SECTION_CONFIG',
    clientId: section._clientId,
    patch,
  });

  // Fetch public tables list
  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ['tableList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const allTables = tablesData?.tables || tablesData?.results || [];
  const publicTables = allTables.filter(t => t.visibility === 'public');
  const selectedTable = publicTables.find(t => t.slug === cfg.table_slug || t.name === cfg.table_slug);

  const BG_PRESETS = [
    { label: 'None',       color: ''        },
    { label: 'White',      color: '#ffffff'  },
    { label: 'Light',      color: '#f8fafc'  },
    { label: 'Light Blue', color: '#eff6ff'  },
    { label: 'Primary',    color: primaryHex },
    { label: 'Dark',       color: '#1e293b'  },
  ];

  return (
    <div>

      {/* ── Source Table ─────────────────────────────────────────────────── */}
      <SectionDivider label="Source" />

      <EditorField label="Public Table" hint="Only tables with Public visibility are shown.">
        {tablesLoading ? (
          <div className="text-muted small">Loading tables…</div>
        ) : publicTables.length === 0 ? (
          <div style={{ fontSize: 12, color: '#ef4444', padding: '6px 10px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca' }}>
            <i className="bi bi-exclamation-triangle me-1" />
            No public tables found. Set a table's visibility to "Public" in the table settings.
          </div>
        ) : (
          <select
            className="form-select form-select-sm"
            value={cfg.table_slug || ''}
            onChange={e => update({ table_slug: e.target.value })}
          >
            <option value="">— Select a table —</option>
            {publicTables.map(t => (
              <option key={t.id} value={t.slug || t.name}>
                {t.display_name || t.name}
              </option>
            ))}
          </select>
        )}
      </EditorField>

      {cfg.table_slug && selectedTable && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: -8, marginBottom: 14, padding: '5px 8px', background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
          <i className="bi bi-link-45deg me-1" />
          Public URL: <strong>/p/{cfg.table_slug}</strong>
        </div>
      )}

      {/* ── Display Config ───────────────────────────────────────────────── */}
      <SectionDivider label="Display" />

      <EditorField label="View Type" hint="Leave as 'Table Default' to use the layout configured in Public Pages.">
        <select
          className="form-select form-select-sm"
          value={cfg.view_type_override || ''}
          onChange={e => update({ view_type_override: e.target.value })}
        >
          {VIEW_TYPES.map(v => (
            <option key={v.value} value={v.value}>{v.label}</option>
          ))}
        </select>
      </EditorField>

      <EditorField label="Initial Records">
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          {COUNT_PRESETS.map(n => (
            <button
              key={n}
              type="button"
              onClick={() => update({ initial_count: n })}
              style={{
                flex: 1, padding: '4px 0', fontSize: 12, fontWeight: 600,
                border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer',
                background: (cfg.initial_count || 6) === n ? primaryHex : '#fff',
                color: (cfg.initial_count || 6) === n ? '#fff' : '#374151',
              }}
            >
              {n}
            </button>
          ))}
        </div>
        <input
          type="number"
          className="form-control form-control-sm"
          min={1} max={100}
          value={cfg.initial_count || 6}
          onChange={e => update({ initial_count: parseInt(e.target.value) || 6 })}
          placeholder="Custom…"
        />
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="ppDetailEnabled"
            checked={cfg.detail_enabled !== false}
            onChange={e => update({ detail_enabled: e.target.checked })} />
          <label className="form-check-label" htmlFor="ppDetailEnabled" style={{ fontSize: 13 }}>
            Allow clicking records to open detail page
          </label>
        </div>
      </EditorField>

      <EditorField label="">
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="ppShowHeader"
            checked={cfg.show_page_header || false}
            onChange={e => update({ show_page_header: e.target.checked })} />
          <label className="form-check-label" htmlFor="ppShowHeader" style={{ fontSize: 13 }}>
            Show public page header (title, record count)
          </label>
        </div>
      </EditorField>

      {/* ── Styling ──────────────────────────────────────────────────────── */}
      <SectionDivider label="Styling" />

      <EditorField label="Background Color">
        <ColorPicker
          value={cfg.bg_color || ''}
          onChange={val => update({ bg_color: val })}
          presets={BG_PRESETS}
        />
      </EditorField>

      <EditorField label="Container Padding">
        <select
          className="form-select form-select-sm"
          value={cfg.padding || 'none'}
          onChange={e => update({ padding: e.target.value })}
        >
          <option value="none">None</option>
          <option value="sm">Small</option>
          <option value="md">Medium</option>
          <option value="lg">Large</option>
        </select>
      </EditorField>

      {/* ── Section heading + Show More ───────────────────────────────────── */}
      <DataSectionPanel cfg={cfg} updateCfg={update} tableSlug={cfg.table_slug} />

    </div>
  );
}
